import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UserRole } from "@farnsworth/shared";
import { api } from "../lib/api.js";
import type { AdminUser } from "../types.js";

export interface UserListResponse {
  data: AdminUser[];
  meta: { total: number; limit: number; offset: number };
}

export interface CreateUserInput {
  email: string;
  username: string;
  password: string;
  role: UserRole;
}

/** Fields an admin can change on an existing account. */
export type UserPatch = Partial<Pick<AdminUser, "email" | "username" | "role" | "isActive">>;

export interface UserListParams {
  /** Free-text match on email/username, forwarded to the API's `q`. */
  q?: string;
  limit?: number;
  offset?: number;
}

export function useUsers({ q = "", limit = 50, offset = 0 }: UserListParams = {}) {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (q) query.set("q", q);

  return useQuery({
    // Keyed on the parameters so each page/search gets its own cache entry
    // instead of one blob that silently serves page 1 forever.
    queryKey: ["users", { q, limit, offset }],
    queryFn: () => api.get<UserListResponse>(`/users?${query.toString()}`),
    placeholderData: (previous) => previous,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api.post<{ data: AdminUser }>("/users", input).then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UserPatch }) =>
      api.patch<{ data: AdminUser }>(`/users/${id}`, patch).then((response) => response.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post(`/users/${id}/password`, { password }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
